import { FiChevronRight } from 'react-icons/fi'
import { NavLink } from 'react-router-dom'

import { ButtonNavLink } from '~/components/Buttons'
import Status from '~/components/Status'
import { formatDateToString } from '~/helpers'
import i18n from '~/i18n'
import { campaignStatusBadge } from '~/modules/retailMedia/helpers/campaignStatusBadge'

const { t } = i18n

/**
 * Leva o escopo da fila para a tela de revisão. Sem ele, o "voltar" de lá cai
 * numa fila sem publisher enquanto o detalhe não chegou — e é exatamente quando
 * o GET falha que aquela é a única saída oferecida. O id vem do gate, não do
 * `rmid` cru da URL, para um decoy em white label não desalinhar a revisão.
 */
const reviewPath = (id: string, rmid?: string | null) => {
  const scope = rmid ? `?rmid=${rmid}` : ''

  return `/retail-media/campaigns/offsite-review/${id}${scope}`
}

type SchemaParams = {
  /** Publisher já resolvido pelo gate (não o `rmid` cru da URL). */
  rmid?: string | null
}

export const schema: (
  params?: SchemaParams
) => TableSchema<FormatOffsiteAdvertiserReviewList> = ({ rmid } = {}) => ({
  campaignName: {
    title: t('rm:Campaign'),
    render: rowData => (
      <NavLink to={reviewPath(rowData.id, rmid)} title={t('rm:clickToReview')}>
        <span className="title fw-medium">{rowData.title}</span>
      </NavLink>
    ),
    className: 'order__details col__infos col__title'
  },
  /**
   * A coluna de varejista de destino fica de fora enquanto a fila não devolver
   * `destination_retailer` — sem data prevista. Renderizá-la agora seria uma
   * coluna inteira de travessões, que ocupa largura e não informa nada.
   *
   * O resto do caminho continua montado: o DTO mapeia o campo, o tipo o declara
   * como opcional e a chave de i18n existe nos três idiomas. Voltar a exibir é
   * reintroduzir estas quatro linhas.
   */
  channel: {
    title: t('rm:offsiteAdvertiserReview.column.channel'),
    render: rowData => rowData.channel
  },
  status: {
    title: t('rm:Status'),
    render: rowData => <Status status={campaignStatusBadge(rowData.status)} />
  },
  createdAt: {
    title: t('rm:creationDate'),
    render: rowData =>
      formatDateToString(rowData.createdAt, 'DD/MM/YY [às] HH:mm')
  },
  start: {
    title: t('rm:Start'),
    render: rowData => rowData.startDate
  },
  end: {
    title: t('rm:End'),
    render: rowData => rowData.endDate
  },
  reviewCampaign: {
    className: 'nowrap',
    render: rowData => (
      <ButtonNavLink
        to={reviewPath(rowData.id, rmid)}
        size="small"
        text={t('rm:reviewCampaign')}
        template="secondary"
        iconRight={<FiChevronRight />}
      />
    )
  }
})
